import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AnalyticsService } from 'src/app/shared/analytics.service';
import {
  OnboardingStepName,
  OnboardingStepStatus,
  TeacherOnboardingService,
} from '../teacher-onboarding.service';
import { cancellable } from 'src/app/shared/cancellable.util';

interface ChecklistItem {
  id: OnboardingStepName;
  label: string;
  icon: string;
  status: OnboardingStepStatus;
}

@Component({
  selector: 'app-onboarding-fab',
  templateUrl: './onboarding-fab.component.html',
  styleUrls: ['./onboarding-fab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingFabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  open = false;

  constructor(
    private onboarding: TeacherOnboardingService,
    private analytics: AnalyticsService,
    private router: Router,
  ) {}

  get visible(): boolean {
    return this.onboarding.isChecklistVisible();
  }

  async ngOnInit(): Promise<void> {
    if (!this.onboarding.canOnboard()) return;
    // Seed from the userInfo cookie (first paint), then refresh from the dedicated
    // endpoint — never touching the cookie.
    this.onboarding.seedFromUserInfo();
    await cancellable(this.onboarding.refreshStatus$(), this.destroyRef);
  }

  get items(): ChecklistItem[] {
    const steps = this.onboarding.status?.steps;
    if (!steps) return [];
    return [
      {
        id: 'subjects',
        label: 'Choose subjects',
        icon: 'menu_book',
        status: steps.subjects,
      },
      {
        id: 'groups',
        label: 'Create a group',
        icon: 'class',
        status: steps.groups,
      },
      {
        id: 'inviteTeam',
        label: 'Invite your team',
        icon: 'group_add',
        status: steps.inviteTeam,
      },
    ];
  }

  get doneCount(): number {
    return this.items.filter(
      (i) => i.status === 'done' || i.status === 'skipped',
    ).length;
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) this.onboarding.refreshStatus();
  }

  resume(): void {
    this.open = false;
    this.analytics.track('onboarding_resumed', { done: this.doneCount });
    this.router.navigate(['/teacher-onboarding']);
  }
}
